/* Pilatoes gallery — swipe track + thumbnail strip.
   Hand-built, so it owes nothing to the theme's own media-gallery component.

   Markup contract (see pilatoes-product.liquid):
     <pilatoes-gallery>
       [data-plt-track]  scroll-snap container, one [data-plt-slide] per media
       [data-plt-thumbs] optional strip of <button data-plt-thumb data-index="n">
       [data-plt-dots]   optional row of <button data-plt-dot data-index="n">
       [data-plt-count]  optional "2 / 5" readout
       [data-plt-zoom]   optional button per photo slide: opens the full-screen viewer (Coach 27 Oct)
*/
(function () {
  if (window.customElements && customElements.get('pilatoes-gallery')) return;

  // UGC video slide (reviews/UGC agent, snippets/pilatoes-ugc-gallery-tile.liquid): a normal slide, never in the photo viewer
  const UGC = '[data-plt-ugc], [data-plt-ugc-slide]';

  class PilatoesGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-plt-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-plt-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-plt-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-plt-dot]'));
      this.countEl = this.querySelector('[data-plt-count]');
      this.index = 0;

      this.thumbs.concat(this.dots).forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(Number(btn.dataset.index), true));
      });

      // Active slide is whichever is nearest the track's centre after scrolling settles.
      this._onScroll = () => {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.sync());
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1, true);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1, true);
      });

      this.bindDrag();
      this.bindZoom();
      this.sync();
    }

    // Desktop (>= 1024 px, clouds-theme PDP desktop A, Coach 27 Oct): click and drag the photo to the next one with a
    // mouse. Trackpad and touch keep their native scroll; on release the track snaps to the next or previous photo
    // once the drag passes 12 % of the photo width, otherwise back to the current one. A click after a drag is swallowed.
    bindDrag() {
      const desk = window.matchMedia('(min-width: 1024px)');
      const track = this.track;
      let x0 = null;
      let s0 = 0;
      let start = 0;
      let moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (!desk.matches || e.pointerType !== 'mouse' || e.button !== 0 || this.slides.length < 2) return;
        // UGC video slide (data-plt-ugc): its own controls keep the mouse; drag anywhere else on it as on a photo
        if (e.target.closest(':is([data-plt-ugc], [data-plt-ugc-slide]) :is(video, button, a, input, [role="slider"])')) return;
        x0 = e.clientX;
        s0 = track.scrollLeft;
        start = this.nearest();
        moved = false;
        track.classList.add('is-drag');
        track.setPointerCapture(e.pointerId);
        e.preventDefault();
      });
      track.addEventListener('pointermove', (e) => {
        if (x0 === null) return;
        const d = e.clientX - x0;
        if (Math.abs(d) > 3) moved = true;
        track.scrollLeft = s0 - d;
      });
      const end = (e) => {
        if (x0 === null) return;
        const d = e.type === 'pointercancel' ? 0 : e.clientX - x0;
        x0 = null;
        track.classList.remove('is-drag');
        const far = Math.abs(d) > track.clientWidth * 0.12;
        this.goTo(far ? start + (d < 0 ? 1 : -1) : start, true);
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    }

    // Full-screen viewer (clouds-theme polish A, Coach 27 Oct): tap/click a photo -> a modal <dialog> on Pilatoes white
    // with every photo; swipe / mouse drag / arrow keys between them, pinch-zoom on touch, 44 px close (X), Esc closes.
    // showModal() makes the page behind inert (focus stays inside); focus returns to the photo button on close.
    // No visible words: the close button's label is Savor's actions.close (data-close-label on <pilatoes-gallery>).
    bindZoom() {
      this.zoomBtns = Array.from(this.querySelectorAll('[data-plt-zoom]'));
      if (!this.zoomBtns.length || typeof HTMLDialogElement === 'undefined') return;
      this.track.addEventListener('click', (e) => {
        if (e.defaultPrevented) return;
        const btn = e.target.closest('[data-plt-zoom]');
        // a desktop mouse click lands on the track (pointer capture): open the photo on screen
        const i = btn ? Number(btn.dataset.index) : this.nearest();
        // the UGC slide is a video, not a photo: never opens the photo viewer
        if (!this.slides[i] || this.slides[i].matches(UGC) || e.target.closest(UGC)) return;
        if (!btn && !this.slides[i].querySelector('[data-plt-zoom]')) return;
        this.openZoom(i, btn || this.slides[i].querySelector('[data-plt-zoom]'));
      });
    }

    buildZoom() {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      const dlg = document.createElement('dialog');
      dlg.className = 'plt-zoom';
      dlg.setAttribute('aria-label', this.track.getAttribute('aria-label') || '');
      const strip = document.createElement('div');
      strip.className = 'plt-zoom__track';
      this.zoomMap = [];
      this.slides.forEach((slide, i) => {
        const src = slide.querySelector('img:not(.plt-badge)');
        if (slide.matches(UGC) || !src || !slide.querySelector('[data-plt-zoom]')) return;
        const cell = document.createElement('div');
        cell.className = 'plt-zoom__slide';
        const img = document.createElement('img');
        img.alt = src.alt || '';
        img.decoding = 'async';
        img.draggable = false;
        if (src.srcset) { img.srcset = src.srcset; img.sizes = '100vw'; }
        img.src = src.currentSrc || src.src;
        cell.appendChild(img);
        strip.appendChild(cell);
        this.zoomMap.push(i);
      });
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'plt-zoom__close';
      close.setAttribute('aria-label', this.dataset.closeLabel || '');
      close.innerHTML = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z" fill="currentColor"/></svg>';
      close.addEventListener('click', () => dlg.close());
      dlg.append(strip, close);
      this.appendChild(dlg);

      const z = { dlg, strip, i: 0, s: 1, tx: 0, ty: 0, dx: 0, pts: new Map(), mode: null };
      this.zoom = z;
      const cells = () => strip.children;
      const img = () => cells()[z.i] && cells()[z.i].firstChild;
      const W = () => strip.clientWidth;
      const H = () => strip.clientHeight;
      const clamp = () => {
        z.tx = Math.min(0, Math.max(W() - z.s * W(), z.tx));
        z.ty = Math.min(0, Math.max(H() - z.s * H(), z.ty));
      };
      const paintImg = () => { const el = img(); if (el) el.style.transform = z.s === 1 ? '' : `translate(${z.tx}px, ${z.ty}px) scale(${z.s})`; };
      const paintStrip = (animate) => {
        strip.classList.toggle('is-moving', !!animate && !reduce.matches);
        strip.style.transform = `translate3d(calc(${-z.i * 100}% + ${z.dx}px), 0, 0)`;
      };
      const resetZoom = () => { z.s = 1; z.tx = 0; z.ty = 0; paintImg(); };
      z.go = (n, animate) => {
        const max = cells().length - 1;
        const next = Math.max(0, Math.min(max, n));
        if (next !== z.i) resetZoom();
        z.i = next; z.dx = 0;
        paintStrip(animate);
      };
      z.resetZoom = resetZoom;
      z.paintStrip = paintStrip;

      const local = (e) => { const r = dlg.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };   // the strip itself is translated
      const two = () => { const [a, b] = Array.from(z.pts.values()); return { d: Math.hypot(a[0] - b[0], a[1] - b[1]) || 1, m: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] }; };
      const start = () => {
        z.s0 = z.s; z.tx0 = z.tx; z.ty0 = z.ty;
        if (z.pts.size >= 2) { const t = two(); z.d0 = t.d; z.m0 = t.m; z.mode = 'pinch'; z.dx = 0; paintStrip(false); }
        else if (z.pts.size === 1) { z.p0 = Array.from(z.pts.values())[0]; z.mode = z.s > 1 ? 'pan' : 'swipe'; }
      };
      strip.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        strip.setPointerCapture(e.pointerId);
        z.pts.set(e.pointerId, local(e));
        start();
        e.preventDefault();
      });
      strip.addEventListener('pointermove', (e) => {
        if (!z.pts.has(e.pointerId)) return;
        z.pts.set(e.pointerId, local(e));
        if (z.mode === 'pinch' && z.pts.size >= 2) {
          const t = two();
          z.s = Math.max(1, Math.min(4, z.s0 * t.d / z.d0));
          z.tx = t.m[0] - z.s * (z.m0[0] - z.tx0) / z.s0;
          z.ty = t.m[1] - z.s * (z.m0[1] - z.ty0) / z.s0;
          clamp(); paintImg();
        } else if (z.mode === 'pan') {
          const p = z.pts.get(e.pointerId);
          z.tx = z.tx0 + p[0] - z.p0[0]; z.ty = z.ty0 + p[1] - z.p0[1];
          clamp(); paintImg();
        } else if (z.mode === 'swipe') {
          z.dx = z.pts.get(e.pointerId)[0] - z.p0[0];
          paintStrip(false);
        }
      });
      const end = (e) => {
        if (!z.pts.has(e.pointerId)) return;
        const wasSwipe = z.mode === 'swipe';
        z.pts.delete(e.pointerId);
        if (wasSwipe && z.pts.size === 0) {
          const d = e.type === 'pointercancel' ? 0 : z.dx;
          const far = Math.abs(d) > W() * 0.12;
          z.dx = 0;
          z.go(far ? z.i + (d < 0 ? 1 : -1) : z.i, true);
        }
        if (z.s < 1.01) resetZoom();
        z.mode = null;
        if (z.pts.size) start();
      };
      strip.addEventListener('pointerup', end);
      strip.addEventListener('pointercancel', end);
      dlg.addEventListener('keydown', (e) => {
        e.stopPropagation();   // the page gallery behind listens for arrows too
        if (e.key === 'ArrowRight') { e.preventDefault(); z.go(z.i + 1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); z.go(z.i - 1, true); }
      });
      dlg.addEventListener('close', () => {
        document.documentElement.classList.remove('plt-zoom-open');
        resetZoom();
        const idx = this.zoomMap[z.i];
        if (idx !== undefined) this.goTo(idx, false);
        const back = this.slides[idx] && this.slides[idx].querySelector('[data-plt-zoom]');
        const target = back || z.opener;
        if (target) target.focus({ preventScroll: true });
      });
      window.addEventListener('resize', () => { if (dlg.open) { resetZoom(); paintStrip(false); } });
    }

    openZoom(index, opener) {
      if (!this.zoom) this.buildZoom();
      const z = this.zoom;
      if (!z || z.dlg.open) return;
      z.opener = opener;
      const at = this.zoomMap.indexOf(index);
      z.i = at < 0 ? 0 : at;
      z.dx = 0;
      z.resetZoom();
      z.paintStrip(false);
      document.documentElement.classList.add('plt-zoom-open');
      z.dlg.showModal();
      z.dlg.querySelector('.plt-zoom__close').focus();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
    }

    nearest() {
      const mid = this.track.scrollLeft + this.track.clientWidth / 2;
      let best = 0;
      let bestGap = Infinity;
      this.slides.forEach((slide, i) => {
        const gap = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - mid);
        if (gap < bestGap) {
          bestGap = gap;
          best = i;
        }
      });
      return best;
    }

    goTo(index, smooth) {
      const max = this.slides.length - 1;
      const next = Math.max(0, Math.min(max, index));
      const slide = this.slides[next];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      this.setIndex(next);
    }

    sync() {
      this.setIndex(this.nearest());
    }

    setIndex(index) {
      this.index = index;
      this.thumbs.forEach((btn, i) => {
        const on = i === index;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-current', on ? 'true' : 'false');
      });
      this.dots.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === index);
      });
      (this.zoomBtns || []).forEach((btn) => {
        btn.tabIndex = Number(btn.dataset.index) === index ? 0 : -1;
      });
      if (this.countEl) {
        this.countEl.textContent = index + 1 + ' / ' + this.slides.length;
      }
      // Keep the active thumbnail in view without touching page scroll.
      const thumb = this.thumbs[index];
      if (thumb && thumb.parentElement) {
        const strip = thumb.parentElement;
        const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }
  }

  customElements.define('pilatoes-gallery', PilatoesGallery);
})();
